import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  AngleDown,
  ButtonV2 as Button,
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraHStack,
  ChakraSimpleGrid,
  DateFormat,
  DatePicker,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Menu,
  MenuOption,
  MenuSelect,
  MenuSelectOption,
  NumberInput,
  TextInput,
} from '@equip.health/ui';

import ErrorMessage from '~/components/common/ErrorMessage';
import {
  GOAL_STATUS,
  GoalFrequency,
  ObservationGoals,
  WeightGoalTypes,
} from '~/lib/constants/goals.constants';
import {
  checkForOverlappingGoalDates,
  getAssigneeIdsFromNames,
  getAssigneeNamesFromIds,
} from '~/lib/util/goals.util';
import { toMenuOption, toMenuSelectOption } from '~/lib/util/menu';
import { getTaskAssigneeName } from '~/lib/util/patient.util';
import { useGetAppUsersQuery } from '~/store/services/appUsers';
import {
  useSaveObservationGoalMutation,
  useUpdateObservationGoalMutation,
} from '~/store/services/clinicalData';
import {
  useGetObservationGoalFrequenciesEnumQuery,
  useGetObservationGoalMethodsEnumQuery,
  useGetObservationGoalStatusesEnumQuery,
  useGetObservationGoalTypesEnumQuery,
} from '~/store/services/enumerations';
import { DateTime } from 'luxon';

const { neutral, primary } = EquipUIFireflyV2Theme.colors;
const { label } = EquipUITheme.typography;

type EditWeightGoalItemProps = {
  goalData?: PatientWeightGoalType;
  goalsList?: PatientWeightGoalType[];
  patientId: string;
  onCancel: () => void;
  onSave: () => void;
};
type EditWeightGoalItemErrors = {
  endDate?: boolean;
  freqReason?: boolean;
  goalAssignees?: boolean;
  goalFreq?: boolean;
  goalMethod?: boolean;
  goalStatus?: boolean;
  goalType?: boolean;
  highBound?: boolean;
  lowBound?: boolean;
  startDate?: boolean;
};
const newWeightGoalStyles = {
  border: `1px solid ${neutral.line}`,
  borderRadius: '16px',
  marginBottom: '8px',
  padding: '24px',
};

const defaultGoalFrequency = GoalFrequency.TWICE_WEEKLY;

const EditWeightGoalItem = ({
  goalData,
  goalsList,
  onCancel,
  onSave,
  patientId,
}: EditWeightGoalItemProps) => {
  const TOP_LEVEL_ID = `edit-goal-${
    goalData?.observationGoalExternalId || 'new'
  }`;

  const {
    data: observationGoalFrequencies,
    isLoading: isGetObservationGoalFrequenciesLoading,
  } = useGetObservationGoalFrequenciesEnumQuery();

  const {
    data: observationGoalMethods,
    isLoading: isGetObservationGoalMethodsLoading,
  } = useGetObservationGoalMethodsEnumQuery();

  const {
    data: observationGoalTypes,
    isLoading: isGetObservationGoalTypesLoading,
  } = useGetObservationGoalTypesEnumQuery();

  const {
    data: observationGoalStatuses,
    isLoading: isGetObservationGoalStatusesLoading,
  } = useGetObservationGoalStatusesEnumQuery();

  const {
    goalEndDate,
    goalFrequencyReason,
    goalAssigneeIds,
    goalFrequency,
    goalMethod,
    goalStatus,
    goalType: goalTypeName,
    highBound: goalHighBound,
    lowBound: goalLowBound,
    observationGoalExternalId,
    goalStartDate,
  } = goalData || {};
  const [type, setType] = useState<string>(goalTypeName ?? null);
  const [method, setMethod] = useState<string>(goalMethod ?? null);
  const [frequency, setFrequency] = useState<string>(
    goalFrequency ?? defaultGoalFrequency,
  );

  const [startDate, setStartDate] = useState<Date | null>(
    goalStartDate ? DateTime.fromISO(goalStartDate).toJSDate() : null,
  );

  const { data: taskAssignees = [] } = useGetAppUsersQuery(
    { includeGeneral: true, includeInactive: true, patientId },
    {
      skip: !patientId,
    },
  );

  const proxyUsers = taskAssignees.filter(
    (each) => each.isProxy && each.accountActive,
  );

  //Assigning proxy users as default value if a new goal is added
  const [assignees, setAssignees] = useState<string[]>(
    goalData
      ? getAssigneeNamesFromIds(goalAssigneeIds, taskAssignees)
      : proxyUsers.map((taskAssignee) => getTaskAssigneeName(taskAssignee)),
  );

  //TODO: store all field errors in a single state variable, with type EditWeightGoalItemErrors
  const [isStartDateValid, setIsStartDateValid] = useState<boolean>(true);
  const [endDate, setEndDate] = useState<Date | null>(
    goalEndDate ? DateTime.fromISO(goalEndDate).toJSDate() : null,
  );
  const [isEndDateValid, setIsEndDateValid] = useState<boolean>(true);
  const [lowBoundary, setLowBoundary] = useState<number>(goalLowBound);
  const [highBoundary, setHighBoundary] = useState<number>(goalHighBound);
  const [alternateFreqReason, setAlternateFreqReason] = useState<string>(
    goalFrequencyReason ?? null,
  );

  const [status, setStatus] = useState<GOAL_STATUS>(
    (goalStatus as GOAL_STATUS) || GOAL_STATUS.ACTIVE,
  );

  const [
    saveObservationGoal,
    { error: saveGoalError, isLoading: isSaveGoalLoading },
  ] = useSaveObservationGoalMutation();

  const [updateObservationGoal] = useUpdateObservationGoalMutation();
  const isTypeWeightMonitoring = type === WeightGoalTypes.WEIGHT_MONITORING;

  const addGoals = () => {
    if (frequency === defaultGoalFrequency) setAlternateFreqReason(null);

    // name comparison is being done in getAssigneeIdsFromNames
    // trim this potential suffix to avoid dropping inactive assignees from the request
    // in general, this is not good practice but it's super legacy code
    const assigneeNamesExcludingPotentialInactiveSuffix = assignees.map(
      (assigneeName) => assigneeName.replace(' (Inactive)', ''),
    );

    const saveGoalOptions = {
      goalAssigneeIds: getAssigneeIdsFromNames(
        assigneeNamesExcludingPotentialInactiveSuffix,
        taskAssignees,
      ),
      goalEndDate: endDate
        ? DateTime.fromJSDate(endDate).toFormat('yyyy-MM-dd')
        : null,
      goalFrequency: frequency,
      goalFrequencyReason:
        frequency === defaultGoalFrequency ? null : alternateFreqReason,
      goalMethod: method,
      goalStartDate: startDate
        ? DateTime.fromJSDate(startDate).toFormat('yyyy-MM-dd')
        : null,
      goalStatus: status,
      goalType: type,
      highBound: !isTypeWeightMonitoring ? highBoundary : null,
      lowBound: !isTypeWeightMonitoring ? lowBoundary : null,
      typeName: ObservationGoals.WEIGHT,
      unit: 'lbs',
    };

    if (goalData) {
      updateObservationGoal({
        ...saveGoalOptions,
        observationGoalExternalId,
        patientId,
      }).then(() => onSave());
    } else {
      saveObservationGoal({ ...saveGoalOptions, patientId }).then(() =>
        onSave(),
      );
    }
  };

  const typeOptions: MenuOption[] = useMemo<MenuOption[]>(
    () =>
      (
        observationGoalTypes
          ?.filter(
            ({ observationType }) =>
              observationType === ObservationGoals.WEIGHT,
          )
          ?.map(({ goalTypeName }) => goalTypeName) ?? []
      ).map(toMenuOption),
    [observationGoalTypes],
  );
  const statusOptions: MenuOption[] = useMemo<MenuOption[]>(
    () =>
      (
        observationGoalStatuses
          ?.filter(
            ({ observationType, goalStatusName }) =>
              observationType === ObservationGoals.WEIGHT &&
              goalStatusName !== GOAL_STATUS.VOID,
          )
          ?.map(({ goalStatusName }) => goalStatusName) ?? []
      ).map(toMenuOption),
    [observationGoalStatuses],
  );

  const frequencyOptions: MenuOption[] = useMemo<MenuOption[]>(
    () =>
      (
        observationGoalFrequencies
          ?.filter(
            ({ observationType, goalFrequencyName }) =>
              observationType === ObservationGoals.WEIGHT &&
              goalFrequencyName !== GoalFrequency.QUARTERLY,
          )
          ?.map(({ goalFrequencyName }) => goalFrequencyName) ?? []
      ).map(toMenuOption),
    [observationGoalFrequencies],
  );
  const methodOptions: MenuOption[] = useMemo<MenuOption[]>(
    () =>
      (
        observationGoalMethods
          ?.filter(
            ({ observationType }) =>
              observationType === ObservationGoals.WEIGHT,
          )
          ?.map(({ goalMethodName }) => goalMethodName) ?? []
      ).map(toMenuOption),
    [observationGoalMethods],
  );

  const assigneeOptions = useMemo<MenuSelectOption[]>(
    () =>
      taskAssignees
        .filter((taskAssignee) => taskAssignee.accountActive)
        .map((taskAssignee) =>
          toMenuSelectOption(getTaskAssigneeName(taskAssignee)),
        ),
    [taskAssignees],
  );

  const isStatusComplete = status === GOAL_STATUS.COMPLETE;
  const isStatusVoid = status === GOAL_STATUS.VOID;

  const isDatesOverlapping = useMemo<boolean>(
    () =>
      !isStatusVoid &&
      checkForOverlappingGoalDates(
        observationGoalExternalId,
        startDate
          ? DateTime.fromJSDate(startDate).toFormat(
              DateFormat.shortYearMonthDay,
            )
          : null,
        endDate
          ? DateTime.fromJSDate(endDate).toFormat(DateFormat.shortYearMonthDay)
          : null,
        goalsList,
      ),
    [startDate, endDate, observationGoalExternalId, goalsList, isStatusVoid],
  );

  const checkRequiredFields = useCallback((): boolean => {
    const newErrors: EditWeightGoalItemErrors = {
      endDate:
        (isStatusComplete && !endDate) || !isEndDateValid || isDatesOverlapping,
      freqReason: !(
        isStatusVoid ||
        frequency === defaultGoalFrequency ||
        (frequency !== defaultGoalFrequency && alternateFreqReason)
      ),
      goalAssignees: !(isStatusVoid || assignees.length !== 0),
      goalFreq: !(isStatusVoid || frequency),
      goalMethod: !(isStatusVoid || method),
      goalStatus: !status,
      goalType: !(isStatusVoid || type),
      startDate: !(startDate && isStartDateValid) || isDatesOverlapping,
    };
    return Object.values(newErrors).some(
      (isError: boolean): boolean => isError,
    );
  }, [
    alternateFreqReason,
    assignees,
    endDate,
    frequency,
    isDatesOverlapping,
    isEndDateValid,
    isStartDateValid,
    isStatusComplete,
    isStatusVoid,
    method,
    startDate,
    status,
    type,
  ]);

  const handleLowBoundary = (val: string) => {
    if (val) {
      setLowBoundary(Number(val));
    } else {
      setLowBoundary(null);
      setHighBoundary(null);
    }
  };

  const isSaveDisabled = checkRequiredFields();

  const handleMultiSelect =
    <T,>(setState: Dispatch<SetStateAction<T>>) =>
    (value: MenuSelectOption | MenuSelectOption[]) => {
      setState(
        !Array.isArray(value)
          ? ([value.value] as unknown as T)
          : (value.reduce((a, o) => [...a, o.value], []) as T),
      );
    };

  const numberInputProps = {
    backgroundColor: 'white',
    color: 'neutral.primary',
    height: '30px',
    inputBoxBorder: `1px solid ${neutral.line}`,
    inputBoxBorderRadius: '8px',
    inputPadding: '14px 12px',
    labelColor: 'neutral.primary',
    labelFontSize: '12px',
    labelFontWeight: 400,
    labelLineHeight: '21.6px',
    labelMarginLeft: '0px',
    size: 'xs' as 'xs' | 'sm' | 'md' | 'lg',
    units: 'lbs',
  };

  const commonMenuProps = {
    borderRadius: '8px',
    buttonHeight: '30px',
    buttonSize: 'xs' as 'xs' | 'sm' | 'md' | 'lg',
    color: 'neutral.primary',
    formLabelFontSize: '12px',
    formLabelFontWeight: '400',
    formLabelMarginLeft: '0px',
    isFullWidth: true,
    textAlign: 'left' as const,
  };

  const commonDatePickerProps = {
    ...label,
    background: 'white',
    customHeight: '14px',
    customIconColor: primary[100],
    customIconHeight: '16px',
    formLabelColor: 'neutral.primary',
    formLabelMarginLeft: '0px',
    inputBoxBorder: `1px solid ${neutral.line}`,
    inputBoxBorderRadius: '8px',
    inputPaddingLeft: '8px',
    isRightIcon: true,
  };

  return (
    <ChakraBox
      {...(goalData ? {} : { ...newWeightGoalStyles })}
      id={TOP_LEVEL_ID}
      width="100%"
    >
      <ChakraSimpleGrid columns={4} spacing="8px">
        <ChakraGridItem colSpan={1}>
          <ChakraFlex flexDirection="column" gridGap="16px">
            <Menu
              {...commonMenuProps}
              buttonContent={type}
              icon={
                <AngleDown fill={primary[100]} height="12px" width="12px" />
              }
              id={`${TOP_LEVEL_ID}__type-select`}
              isDisabled={isGetObservationGoalTypesLoading || isSaveGoalLoading}
              isLoading={isGetObservationGoalTypesLoading}
              isRequired={!isStatusVoid}
              label="Type"
              onSelect={setType}
              options={typeOptions}
            />
            <DatePicker
              {...commonDatePickerProps}
              id={`${TOP_LEVEL_ID}__start-date-select`}
              isInvalid={startDate && (!isStartDateValid || isDatesOverlapping)}
              isRequired={!isStatusVoid}
              label="Start date"
              maxDate={endDate ?? null}
              onSelect={setStartDate}
              onValidateDate={(isValid: boolean): void =>
                setIsStartDateValid(isValid)
              }
              secondaryErrorMessage={
                isStartDateValid && startDate && isDatesOverlapping
                  ? 'Goal dates cannot overlap.'
                  : null
              }
              value={startDate}
            />
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraGridItem colSpan={1}>
          <ChakraFlex flexDirection="column" gridGap="16px">
            <Menu
              {...commonMenuProps}
              buttonContent={frequency}
              icon={
                <AngleDown fill={primary[100]} height="12px" width="12px" />
              }
              id={`${TOP_LEVEL_ID}__goal-frequency`}
              isDisabled={
                isGetObservationGoalFrequenciesLoading || isSaveGoalLoading
              }
              isLoading={isGetObservationGoalFrequenciesLoading}
              isRequired={!isStatusVoid}
              label="Frequency"
              onSelect={setFrequency}
              options={frequencyOptions}
            />

            <DatePicker
              {...commonDatePickerProps}
              id={`${TOP_LEVEL_ID}__end-date-select`}
              isInvalid={endDate && (!isEndDateValid || isDatesOverlapping)}
              isRequired={isStatusComplete}
              label="End date"
              maxDate={isStatusComplete ? new Date() : null}
              minDate={startDate ?? null}
              onSelect={setEndDate}
              onValidateDate={(isValid: boolean): void =>
                setIsEndDateValid(isValid)
              }
              secondaryErrorMessage={
                isEndDateValid && endDate && isDatesOverlapping
                  ? 'Goal dates cannot overlap.'
                  : null
              }
              value={endDate}
            />
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraGridItem colSpan={1}>
          <ChakraFlex flexDirection="column" gridGap="16px">
            <Menu
              {...commonMenuProps}
              buttonContent={method}
              icon={
                <AngleDown fill={primary[100]} height="12px" width="12px" />
              }
              id={`${TOP_LEVEL_ID}__goal-method`}
              isDisabled={
                isGetObservationGoalMethodsLoading || isSaveGoalLoading
              }
              isLoading={isGetObservationGoalMethodsLoading}
              isRequired={!isStatusVoid}
              label="Method"
              onSelect={setMethod}
              options={methodOptions}
            />
            {!isTypeWeightMonitoring && (
              <NumberInput
                {...numberInputProps}
                id={`${TOP_LEVEL_ID}__low-boundary-value`}
                label="Low boundary"
                max={highBoundary || undefined} // TODO this is bad practice, 0 may be a valid value and is falsy
                maxLength={4}
                onChange={handleLowBoundary}
                value={lowBoundary || undefined} // TODO this is bad practice, 0 may be a valid value and is falsy
              />
            )}
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraGridItem colSpan={1}>
          <ChakraFlex flexDirection="column" gridGap="12px">
            <MenuSelect
              {...commonMenuProps}
              backgroundColor="white"
              border={`1px solid ${neutral.line}`}
              formLabel="Assignees"
              formLabelColor="neutral.primary"
              formLabelMarginBottom="12px"
              formLabelPadding="0px"
              id={`${TOP_LEVEL_ID}__assignees`}
              isRequired={!isStatusVoid}
              listMaxHeight="150px"
              minHeight="30px"
              multiple
              onSelect={handleMultiSelect<string[]>(setAssignees)}
              options={assigneeOptions}
              placeholder="Select all that apply..."
              placeholderFontSize="12px"
              placeholderFontWeight="400"
              placeholderMarginLeft="4px"
              selectedValues={
                assignees?.map((value) => ({
                  node: value,
                  value,
                })) ?? []
              }
              selectedValuesMarginBottom="2px"
              selectedValuesPadding="2px 4px"
            />
            {!!(!isTypeWeightMonitoring && lowBoundary) && (
              <NumberInput
                {...numberInputProps}
                id={`${TOP_LEVEL_ID}__high-boundary-value`}
                label="High boundary"
                max={1000}
                maxLength={4}
                min={lowBoundary || (!isStatusVoid ? 10 : undefined)}
                onChange={(val) => {
                  setHighBoundary(val ? Number(val) : null);
                }}
                value={highBoundary || undefined}
              />
            )}
          </ChakraFlex>
        </ChakraGridItem>
      </ChakraSimpleGrid>
      <ChakraSimpleGrid columns={2} marginTop="16px" spacing="8px">
        <ChakraGridItem colSpan={1}>
          <Menu
            {...commonMenuProps}
            buttonContent={status}
            icon={<AngleDown fill={primary[100]} height="12px" width="12px" />}
            id={`${TOP_LEVEL_ID}__status-select`}
            isDisabled={
              isGetObservationGoalStatusesLoading || isSaveGoalLoading
            }
            isLoading={isGetObservationGoalStatusesLoading}
            label="Status"
            onSelect={(newStatus: GOAL_STATUS) => setStatus(newStatus)}
            options={statusOptions}
          />
        </ChakraGridItem>
        {frequency !== defaultGoalFrequency && (
          <ChakraGridItem colSpan={1}>
            <TextInput
              backgroundColor="white"
              border={`1px solid ${neutral.line}`}
              color="neutral.primary"
              formLabelMarginLeft="0px"
              height="30px"
              id={`${TOP_LEVEL_ID}__alternate-frequency-reason`}
              inputBoxBorderRadius="8px"
              isRequired
              label="Alternate frequency reason"
              labelColor="neutral.primary"
              labelFontSize="12px"
              labelFontWeight="400"
              labelLineHeight="21.6px"
              labelPadding="0px"
              onChange={(e) => {
                setAlternateFreqReason(e.target.value);
              }}
              size="xs"
              value={alternateFreqReason}
            />
          </ChakraGridItem>
        )}
      </ChakraSimpleGrid>
      <ChakraHStack alignItems="center" justifyContent="right" marginTop="16px">
        <Button
          fontSize="12px"
          fontWeight="700"
          id={`${TOP_LEVEL_ID}__cancel`}
          isDisabled={isSaveGoalLoading}
          onClick={onCancel}
          size="sm"
          variant="link"
        >
          Discard
        </Button>
        <Button
          fontSize="12px"
          fontWeight="700"
          id={`${TOP_LEVEL_ID}__confirm`}
          isDisabled={isSaveGoalLoading || isSaveDisabled}
          isLoading={isSaveGoalLoading}
          onClick={addGoals}
          size="sm"
          variant="solid"
        >
          Confirm goal
        </Button>
      </ChakraHStack>
      {saveGoalError && (
        <ChakraBox paddingBottom="16px" textAlign="center">
          <ErrorMessage id={`${TOP_LEVEL_ID}__error-saving`}>
            Error saving goal
          </ErrorMessage>
        </ChakraBox>
      )}
    </ChakraBox>
  );
};

export default EditWeightGoalItem;
